/**
 * Appends the current session's access token to a /uploads/... URL as a
 * query param. Native <img>/<audio>/<source> tags can't attach an
 * Authorization header, and /uploads now requires one or the other --
 * see server/middleware.ts. Client-only (reads the in-memory access
 * token from AuthContext); do not import this from anything server-side
 * imports, unlike src/utils/albumArt.ts which is intentionally shared.
 */

import { getAccessToken } from '../context/AuthContext';

/**
 * Some URLs that flow through the same fields (e.g. SunoClip.imageUrl) are
 * external Suno CDN links, not our own /uploads/ files -- only append the
 * token to our own uploads paths, never to a third-party URL.
 */
function isOwnUploadsUrl(url: string): boolean {
  if (url.startsWith('/uploads/')) return true;
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/uploads/')
      && new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function withUploadsToken(url: string): string {
  if (!isOwnUploadsUrl(url)) return url;
  const token = getAccessToken();
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}
